import type {
  AutoRuleTemplateCondition,
  AutoRuleTemplateFormValues,
  AutoRuleTemplateItem,
  AutoRuleTemplateMutationResult,
  AutoRuleTemplateOperationLogItem,
  AutoRuleTemplateOperationLogQuery,
  AutoRuleTemplateResponseItem,
  AutoRuleTemplateUsageCondition,
  AutoRuleTemplateUsageItem,
} from './template-types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

import { getDateTimeRange, omitBlankString, toBackendInt64 } from './convert'

interface TemplatePageRequest {
  condition?: AutoRuleTemplateCondition
  pageIndex: number
  pageSize: number
}
type TemplateLogPageRequest = PagingRequest<{
  condition?: AutoRuleTemplateOperationLogQuery
}>
type TemplateUsagePageRequest = PagingRequest<{
  condition: AutoRuleTemplateUsageCondition
}>

function getTemplateRequest(params: TemplatePageRequest) {
  const createdAt = getDateTimeRange(params.condition?.createdTimeRange)
  return {
    condition: {
      actionTypes: params.condition?.actionTypes,
      createdAtEnd: createdAt.end,
      createdAtStart: createdAt.start,
      monitoringTargets: params.condition?.monitoringTargets,
      name: omitBlankString(params.condition?.templateName?.trim()),
      source: params.condition?.templateSource,
      status: params.condition?.status,
    },
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
  }
}

function mapTemplateItem(
  item: AutoRuleTemplateResponseItem,
): AutoRuleTemplateItem {
  return {
    actionType: item.actionType ?? item.config?.actions?.[0]?.type,
    config: item.config,
    createdAt: item.createdAt,
    creatorName: item.creatorName,
    description: item.description,
    endTime: item.endTime,
    id: item.id,
    name: item.name,
    operatorName: item.updaterName ?? item.operatorName,
    status: item.status,
    startTime: item.startTime,
    templateSource: item.source ?? item.templateSource,
    updatedAt: item.updatedAt,
    usageCount: item.usageCount,
    version: item.version,
  }
}

export const autoRuleTemplateApi = defineApi('operation', ({ prefix }) => {
  /**
   * 分页查询自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/page
   */
  function queryTemplates(params: TemplatePageRequest) {
    return requestClient
      .post<{
        list?: AutoRuleTemplateResponseItem[]
        total?: number
      }>(prefix('adsAutoRuleTemplate/page'), getTemplateRequest(params))
      .then((res) => ({
        items: (res?.list ?? []).map((item) => mapTemplateItem(item)),
        total: res?.total,
      }))
  }

  /**
   * 查询自动规则模板详情。
   * POST /gobestads/operation/adsAutoRuleTemplate/detail
   */
  function queryTemplateDetail(id: number | string) {
    return requestClient
      .post<AutoRuleTemplateResponseItem>(
        prefix('adsAutoRuleTemplate/detail'),
        {
          id: toBackendInt64(id),
        },
      )
      .then(mapTemplateItem)
  }

  /**
   * 创建自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/create
   */
  function createTemplate(params: AutoRuleTemplateFormValues) {
    return requestClient.post<AutoRuleTemplateMutationResult>(
      prefix('adsAutoRuleTemplate/create'),
      params,
    )
  }

  /**
   * 更新自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/update
   */
  function updateTemplate(
    id: number | string,
    version: number,
    params: AutoRuleTemplateFormValues,
  ) {
    return requestClient.post<AutoRuleTemplateMutationResult>(
      prefix('adsAutoRuleTemplate/update'),
      { ...params, id: toBackendInt64(id), version },
    )
  }

  /**
   * 删除自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/delete
   */
  function deleteTemplate(id: number | string, version: number) {
    return requestClient.post<unknown>(prefix('adsAutoRuleTemplate/delete'), {
      id: toBackendInt64(id),
      version,
    })
  }

  /**
   * 启用自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/enable
   */
  function enableTemplate(id: number | string, version: number) {
    return requestClient.post<unknown>(prefix('adsAutoRuleTemplate/enable'), {
      id: toBackendInt64(id),
      version,
    })
  }

  /**
   * 停用自动规则模板。
   * POST /gobestads/operation/adsAutoRuleTemplate/disable
   */
  function disableTemplate(id: number | string, version: number) {
    return requestClient.post<unknown>(prefix('adsAutoRuleTemplate/disable'), {
      id: toBackendInt64(id),
      version,
    })
  }

  /**
   * 分页查询自动规则模板操作日志。
   * POST /gobestads/operation/adsAutoRuleTemplate/operationLog/page
   */
  function queryTemplateOpLogs(params: TemplateLogPageRequest) {
    const operatedAt = getDateTimeRange(params.condition?.operatedTimeRange)
    const condition = {
      ...(operatedAt.end ? { operatedAtEnd: operatedAt.end } : {}),
      ...(operatedAt.start ? { operatedAtStart: operatedAt.start } : {}),
      ...(params.condition?.operationType
        ? { operationType: params.condition.operationType }
        : {}),
      ...(params.condition?.templateId === undefined
        ? {}
        : { templateId: toBackendInt64(params.condition.templateId) }),
    }

    return requestClient
      .post<{ list?: AutoRuleTemplateOperationLogItem[]; total?: number }>(
        prefix('adsAutoRuleTemplate/operationLog/page'),
        {
          condition,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      )
      .then((res) => ({ items: res?.list ?? [], total: res?.total }))
  }

  /**
   * 分页查询系统模板历史使用记录。
   * POST /gobestads/operation/adsAutoRuleTemplate/usage/page
   */
  function queryTemplateUsage(params: TemplateUsagePageRequest) {
    const createdAt = getDateTimeRange(params.condition.createdTimeRange)
    return requestClient
      .post<{ list?: AutoRuleTemplateUsageItem[]; total?: number }>(
        prefix('adsAutoRuleTemplate/usage/page'),
        {
          condition: {
            createdAtEnd: createdAt.end,
            createdAtStart: createdAt.start,
            customerName: omitBlankString(
              params.condition.customerName?.trim(),
            ),
            merchantId: toBackendInt64(params.condition.merchantId),
            ruleKeyword: omitBlankString(params.condition.ruleKeyword?.trim()),
            templateId: toBackendInt64(params.condition.templateId),
          },
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      )
      .then((res) => ({ items: res?.list ?? [], total: res?.total }))
  }

  return {
    createTemplate,
    deleteTemplate,
    disableTemplate,
    enableTemplate,
    queryTemplateDetail,
    queryTemplateOpLogs,
    queryTemplateUsage,
    queryTemplates,
    updateTemplate,
  }
})
